import {
  apiPostFuelMaintenanceReport,
  apiPutFuelMaintenanceReport,
} from "@app/api/machines/machines.api";
import {
  uploadMediaFiles,
  uploadRecording,
  uploadSignature,
  type MachineReportMediaPayload,
  type MachineReportRecordingPayload,
} from "../Inspection/createInspectionSubmit.helper";

export const WORK_AT_MIN_DATE = new Date(2024, 0, 1, 0, 0, 0, 0);

export function isWorkAtInValidRange(value: Date | null | undefined): boolean {
  if (!value) return false;
  const max = new Date();
  return (
    value.getTime() >= WORK_AT_MIN_DATE.getTime() &&
    value.getTime() <= max.getTime()
  );
}

export type CreateRefuelFormDraft = {
  comment: string;
  mediaFiles: File[];
  recordedBlob: Blob | null;
  serviceMeterValue: string;
  odometerValue: string;
  workAt: Date | null;
  fuelSwitchOn: boolean;
  fuelAmountValue: string;
  adblueSwitchOn: boolean;
  adblueAmountValue: string;
};

export type FuelRefillPayload = {
  isFuelRefilled: boolean;
  isAdblueRefilled: boolean;
  comment: string;
  fuelInLiters?: number;
  fuelInLitersAdBlue?: number;
};

export type FuelMaintenanceReportMediaPayload = {
  fileName: string;
  filePath: string;
  mimeType: string;
  captureMediaAt: string;
};

export type FuelMaintenanceReportRecordingPayload = {
  filePath: string;
  fileName: string;
  mimeType: string;
  recordingAt: string;
};

export type CreateFuelMaintenanceReportPayload = {
  serviceMeterInHour: string;
  odometer?: string;
  workAt: string;
  signatureUrl?: string;
  fuelRefill: FuelRefillPayload;
  machineReportMedias: FuelMaintenanceReportMediaPayload[];
  machineReportRecordings: FuelMaintenanceReportRecordingPayload[];
};

export type UpdateFuelMaintenanceReportPayload = {
  serviceMeterInHour: string;
  odometer?: string;
  workAt: string;
  lastUpdatedSignatureUrl?: string;
  fuelRefill: FuelRefillPayload;
  machineReportMedias: FuelMaintenanceReportMediaPayload[];
  machineReportRecordings: FuelMaintenanceReportRecordingPayload[];
};

type ExistingMediaFile = File & {
  mediaUrl?: string;
  fileName?: string;
  mimeType?: string;
  captureMediaAt?: string;
  filePath?: string;
};

export type ExistingRecordingMeta = {
  recordingUrl?: string | null;
  fileName?: string | null;
  filePath?: string | null;
  mimeType?: string | null;
  recordingAt?: string | null;
};

function parseOptionalNumeric(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function extractFilePathFromUrl(url?: string | null): string {
  if (!url) return "";
  const match = url.match(/contents\/([^?]+)/i);
  if (match?.[1]) {
    return decodeURIComponent(match[1]);
  }
  return url;
}

function extractFileNameFromUrl(url?: string | null): string {
  if (!url) return "";
  const path = extractFilePathFromUrl(url);
  const parts = path.split("/");
  return parts[parts.length - 1] || "";
}

function toApiMedia(
  media: MachineReportMediaPayload,
): FuelMaintenanceReportMediaPayload {
  return {
    fileName: media.fileName,
    filePath: media.filePath,
    mimeType: media.mimeType,
    captureMediaAt: media.captureMediaAt,
  };
}

function toApiRecording(
  recording: MachineReportRecordingPayload,
): FuelMaintenanceReportRecordingPayload {
  return {
    filePath: recording.filePath,
    fileName: recording.fileName,
    mimeType: recording.mimeType,
    recordingAt: recording.recordingAt,
  };
}

function buildFuelRefill(draft: CreateRefuelFormDraft): FuelRefillPayload {
  if (!draft.fuelSwitchOn && !draft.adblueSwitchOn) {
    throw new Error("REFILL_REQUIRED");
  }

  const fuelRefill: FuelRefillPayload = {
    isFuelRefilled: draft.fuelSwitchOn,
    isAdblueRefilled: draft.adblueSwitchOn,
    comment: draft.comment.trim(),
  };

  if (draft.fuelSwitchOn) {
    const fuelInLiters = parseOptionalNumeric(draft.fuelAmountValue);
    if (fuelInLiters === undefined) {
      throw new Error("FUEL_AMOUNT_REQUIRED");
    }
    fuelRefill.fuelInLiters = fuelInLiters;
  }

  if (draft.adblueSwitchOn) {
    const fuelInLitersAdBlue = parseOptionalNumeric(draft.adblueAmountValue);
    if (fuelInLitersAdBlue !== undefined) {
      fuelRefill.fuelInLitersAdBlue = fuelInLitersAdBlue;
    }
  }

  return fuelRefill;
}

async function buildUpdateMediaFiles(
  mediaFiles: File[],
): Promise<FuelMaintenanceReportMediaPayload[]> {
  const existing: FuelMaintenanceReportMediaPayload[] = [];
  const newFiles: File[] = [];

  for (const file of mediaFiles) {
    const media = file as ExistingMediaFile;
    if (file instanceof File && !media.mediaUrl) {
      newFiles.push(file);
      continue;
    }

    if (media.mediaUrl) {
      existing.push({
        fileName:
          media.fileName ||
          media.name ||
          extractFileNameFromUrl(media.mediaUrl),
        filePath: media.filePath || extractFilePathFromUrl(media.mediaUrl),
        mimeType: media.mimeType || media.type || "application/octet-stream",
        captureMediaAt: media.captureMediaAt || new Date().toISOString(),
      });
    }
  }

  const uploaded = (await uploadMediaFiles(newFiles)).map(toApiMedia);
  return [...existing, ...uploaded];
}

async function buildUpdateRecordings(
  recordedBlob: Blob | null,
  existingRecording?: ExistingRecordingMeta | null,
  keepExistingRecording = false,
): Promise<FuelMaintenanceReportRecordingPayload[]> {
  if (recordedBlob && !keepExistingRecording) {
    return (await uploadRecording(recordedBlob)).map(toApiRecording);
  }

  if (!existingRecording?.recordingUrl && !existingRecording?.filePath) {
    return [];
  }

  const recordingUrl = existingRecording.recordingUrl || "";
  return [
    {
      fileName:
        existingRecording.fileName ||
        extractFileNameFromUrl(recordingUrl) ||
        "recording.m4a",
      filePath:
        existingRecording.filePath || extractFilePathFromUrl(recordingUrl),
      mimeType: existingRecording.mimeType || "audio/m4a",
      recordingAt: existingRecording.recordingAt || new Date().toISOString(),
    },
  ];
}

export async function submitCreateRefuel(params: {
  groupId: string;
  machineId: string;
  draft: CreateRefuelFormDraft;
  signatureDataUrl: string | null;
}): Promise<{ successMessage?: string }> {
  const { groupId, machineId, draft, signatureDataUrl } = params;

  if (!draft.workAt || !isWorkAtInValidRange(draft.workAt)) {
    throw new Error("WORK_AT_INVALID");
  }

  const serviceMeterInHour = draft.serviceMeterValue.trim();
  if (!serviceMeterInHour) {
    throw new Error("SERVICE_METER_REQUIRED");
  }

  const odometer = draft.odometerValue.trim();

  const signatureUrl = signatureDataUrl
    ? await uploadSignature(signatureDataUrl)
    : undefined;

  const machineReportMedias = (await uploadMediaFiles(draft.mediaFiles)).map(
    toApiMedia,
  );

  const machineReportRecordings = (
    await uploadRecording(draft.recordedBlob)
  ).map(toApiRecording);

  const fuelRefill = buildFuelRefill(draft);

  const payload: CreateFuelMaintenanceReportPayload = {
    serviceMeterInHour,
    ...(odometer ? { odometer } : {}),
    workAt: draft.workAt.toISOString(),
    ...(signatureUrl ? { signatureUrl } : {}),
    fuelRefill,
    machineReportMedias,
    machineReportRecordings,
  };

  const response = await apiPostFuelMaintenanceReport(
    groupId,
    machineId,
    payload,
  );
  return {
    successMessage: response?.data?.meta?.successMessage,
  };
}

export async function submitUpdateRefuel(params: {
  groupId: string;
  machineId: string;
  machineReportId: string;
  draft: CreateRefuelFormDraft;
  signatureDataUrl: string | null;
  existingRecording?: ExistingRecordingMeta | null;
  keepExistingRecording?: boolean;
}): Promise<{ successMessage?: string }> {
  const {
    groupId,
    machineId,
    machineReportId,
    draft,
    signatureDataUrl,
    existingRecording,
    keepExistingRecording = false,
  } = params;

  if (!draft.workAt || !isWorkAtInValidRange(draft.workAt)) {
    throw new Error("WORK_AT_INVALID");
  }

  const serviceMeterInHour = draft.serviceMeterValue.trim();
  if (!serviceMeterInHour) {
    throw new Error("SERVICE_METER_REQUIRED");
  }

  const odometer = draft.odometerValue.trim();

  const lastUpdatedSignatureUrl = signatureDataUrl
    ? await uploadSignature(signatureDataUrl)
    : undefined;

  const machineReportMedias = await buildUpdateMediaFiles(draft.mediaFiles);
  const machineReportRecordings = await buildUpdateRecordings(
    draft.recordedBlob,
    existingRecording,
    keepExistingRecording,
  );

  const fuelRefill = buildFuelRefill(draft);

  const payload: UpdateFuelMaintenanceReportPayload = {
    serviceMeterInHour,
    ...(odometer ? { odometer } : {}),
    workAt: draft.workAt.toISOString(),
    ...(lastUpdatedSignatureUrl ? { lastUpdatedSignatureUrl } : {}),
    fuelRefill,
    machineReportMedias,
    machineReportRecordings,
  };

  const response = await apiPutFuelMaintenanceReport(
    groupId,
    machineId,
    machineReportId,
    payload,
  );

  return {
    successMessage: response?.data?.meta?.successMessage,
  };
}
